import { useEffect } from 'react'
import { useCookies } from 'react-cookie'

import environment from 'environment'

import { useAdFree, useModerator } from 'hooks/adFree'

import { Option } from 'components/elements/ArchidektDropdown/types'

import PhatDropdown from 'components/formElements/PhatDropdown'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Modal from 'components/elements/Modal'
import Link from 'components/elements/Link'

import styles from './themeDropdown.module.scss'

export type ThemeType = 'light' | 'dark'
export type Theme = 'light' | 'dark' | 'oled' | 'brown' | 'green' | 'pink' | 'cerulean' | 'gruul' | 'rakdos'

export const THEME_CLASS_NAMES: Record<Theme, string> = {
  light: '',
  dark: 'dark-mode',
  oled: 'oled',
  brown: 'brown',
  green: 'green',
  pink: 'pink',
  cerulean: 'cerulean',

  // Guilds
  gruul: 'gruul',
  rakdos: 'rakdos',
}

const FREE_THEMES: Partial<Record<Theme, boolean>> = {
  light: true,
  dark: true,
}

const IS_ALT_DARK_MODE_THEME: Partial<Record<Theme, boolean>> = {
  oled: true,
  rakdos: true,
}

const IS_IMMERSIVE_THEME: Partial<Record<Theme, boolean>> = {
  brown: true,
  green: true,
  cerulean: true,
  gruul: true,
  rakdos: true,
}

type Props = { className?: string; fullDropdown?: boolean; up?: boolean }

const ThemeDropdown = ({ className, fullDropdown, up }: Props) => {
  const [{ patreonThemeModalInfo }, setCookies] = useCookies(['patreonThemeModalInfo'])

  const { theme, setTheme, systemThemeEnabled, tempThemeEnabled } = useTheme()

  const isMod = useModerator()

  // useEffect(() => {
  //   if (tempThemeEnabled && !patreonThemeModalInfo) setCookies('patreonThemeModalInfo', 'true', { path: '/' }) // Explicitly no expiration. Use session cookie
  // }, [tempThemeEnabled])

  const options: Option[] = [
    {
      icon: 'sun',
      label: 'Light theme',
      onClick: () => setTheme('light'),
      className: theme === 'light' && !systemThemeEnabled ? styles.selected : undefined,
      id: 'light',
    },
    {
      icon: 'moon',
      label: 'Dark theme',
      onClick: () => setTheme('dark'),
      className: theme === 'dark' && !systemThemeEnabled ? styles.selected : undefined,
      id: 'dark',
    },
    { type: 'spacer' },
    {
      icon: 'circle-half-stroke',
      label: 'System theme',
      onClick: () => setTheme(),
      className: systemThemeEnabled ? styles.selected : undefined,
      id: 'system',
    },
    { type: 'spacer', hidden: !isMod },
    {
      type: 'extras',
      label: 'Patreon themes',
      id: 'patreon-themes',
      icon: 'patreon',
      hidden: !isMod,
      options: [
        {
          label: 'OLED',
          onChange: () => setTheme('oled'),
          className: theme === 'oled' && !systemThemeEnabled ? styles.selected : undefined,
          type: 'checkbox',
          checked: theme === 'oled',
          id: 'oled',
        },
        {
          label: 'Mint',
          onChange: () => setTheme('green'),
          className: theme === 'green' && !systemThemeEnabled ? styles.selected : undefined,
          type: 'checkbox',
          checked: theme === 'green',
          id: 'green',
        },
        {
          label: 'Chocolate',
          onChange: () => setTheme('brown'),
          className: theme === 'brown' && !systemThemeEnabled ? styles.selected : undefined,
          type: 'checkbox',
          checked: theme === 'brown',
          id: 'brown',
        },
        {
          label: 'Rose',
          onChange: () => setTheme('pink'),
          className: theme === 'pink' && !systemThemeEnabled ? styles.selected : undefined,
          type: 'checkbox',
          checked: theme === 'pink',
          id: 'pink',
        },
        {
          label: 'Cerulean',
          onChange: () => setTheme('cerulean'),
          className: theme === 'cerulean' && !systemThemeEnabled ? styles.selected : undefined,
          type: 'checkbox',
          checked: theme === 'cerulean',
          id: 'cerulean',
        },
        {
          label: 'Rakdos',
          onChange: () => setTheme('rakdos'),
          className: theme === 'rakdos' && !systemThemeEnabled ? styles.selected : undefined,
          type: 'checkbox',
          checked: theme === 'rakdos',
          id: 'rakdos',
        },
      ],
    },
  ]

  const PatreonThemeModal = (
    <Modal
      closeButton
      open={patreonThemeModalInfo === 'true' && tempThemeEnabled !== null}
      onClose={() => setCookies('patreonThemeModalInfo', 'false', { path: '/' })}
      modalClassName={styles.patreonThemeModal}>
      <h2>Patreon themes</h2>
      <p>
        You've selected a patreon only theme. We allow these themes to be selected by anyone, so that everyone can see
        what other themes are availible. But if you are not a patreon memeber,{' '}
        <b>the theme you have selected will not be cached, and will reset on reload or changing pages.</b> We hope this
        gives everyone an opportunity to try something before committing to it.
      </p>

      <p>
        If these feel like your jam, please consider subscribing on our{' '}
        <Link href="https://www.patreon.com/archidekt/membership">Patreon</Link> for $2 per month to get ad-free and
        access to these themes.
      </p>
    </Modal>
  )

  if (fullDropdown)
    return (
      <>
        <PhatDropdown
          className={className}
          menuClassName={up ? styles.mobileMenu : undefined}
          header="Site theme"
          value={systemThemeEnabled ? 'system' : theme}
          options={options}
        />

        {/* {PatreonThemeModal} */}
      </>
    )

  return (
    <>
      <ArchidektDropdown
        triggerClassName={className}
        menuClassName={styles.menu}
        triggerIcon="moon"
        options={options}
      />

      {/* {PatreonThemeModal} */}
    </>
  )
}

export default ThemeDropdown

export const useTheme = (): {
  theme: Theme
  setTheme: (value?: Theme) => void
  systemThemeEnabled: boolean
  themeType: ThemeType
  tempThemeEnabled: boolean
} => {
  const adFree = useAdFree()

  const [{ tbTheme, tbSystemTheme: usingSystemTheme, tbTempTheme }, setCookies] = useCookies([
    'tbTheme',
    'tbSystemTheme',
    'tbTempTheme',
  ])

  const systemThemeEnabled = usingSystemTheme === 'true' // Returns boolean if system theme is set

  const themeName = getThemeName(tbTheme) // Ensures that we're returning a clean Theme type (as opposed to an empty string or nothing)
  const tempThemeName = tbTempTheme === 'null' ? null : getThemeName(tbTempTheme)

  const themeType: ThemeType = getThemeType(tempThemeName || themeName) // Return light | dark based on the theme (useful for more generic changes)

  const handleSetTheme = (theme?: Theme) => {
    const themeToCache = getThemeName(theme)
    const isFreeTheme = FREE_THEMES[themeToCache]

    if (!isFreeTheme && !adFree && environment.nodeEnv === 'production')
      setCookies('tbTempTheme', themeToCache, { path: '/' })
    else {
      setCookies('tbTheme', themeToCache, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) })
      setCookies('tbSystemTheme', !theme, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) })
      setCookies('tbTempTheme', 'null', { path: '/' })
    }

    const themeClassName = THEME_CLASS_NAMES[themeToCache]

    const removedClassNames: string[] = Object.keys(THEME_CLASS_NAMES)
      .filter(themeKey => themeKey !== themeToCache)
      // @ts-ignore
      .map(themeKey => THEME_CLASS_NAMES[themeKey])

    for (const className of removedClassNames) {
      if (!className) continue

      document.body.classList.remove(className)
    }

    if (IS_IMMERSIVE_THEME[themeToCache]) document.body.classList.add('immersive-theme')
    else document.body.classList.remove('immersive-theme')

    if (themeClassName) {
      if (IS_ALT_DARK_MODE_THEME[themeToCache]) document.body.classList.add('dark-mode')

      document.body.classList.add(themeClassName)
    }
  }

  useEffect(() => {
    handleSetTheme(systemThemeEnabled ? undefined : tbTheme)
  }, [])

  useEffect(() => {}, [themeName])

  return {
    theme: tempThemeName || themeName,
    setTheme: handleSetTheme,
    systemThemeEnabled,
    themeType,
    tempThemeEnabled: !!tempThemeName,
  }
}

export const getThemeName = (theme: any): Theme => {
  // @ts-ignore - we recognized the cached theme name, return it
  if (typeof THEME_CLASS_NAMES[theme] === 'string') return theme

  // User's browser prefers dark theme
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'

  return 'light'
}

export const getThemeType = (theme: Theme): ThemeType =>
  theme === 'dark' || IS_ALT_DARK_MODE_THEME[theme] ? 'dark' : 'light'
